import React, { useMemo, useState, useEffect } from "react";
import { X, Calendar, Eye } from "lucide-react";
import { useGetPostesEmission } from "../../hooks/emission/useGetPostesEmission";
import { useGetTypeIndicateurs } from "../../hooks/metadata/useGetTypeIndicators";
import { useGetPosteDimensions } from "../../hooks/metadata/useGetPosteDimensions";
import type { Site } from "../../types/site";
import type { TypeIndicateur } from "../../types/typeIndicateurs";
import type { Saisie, DimensionSelection } from "../../types/saisie";
import "./DataEntryDialog.css";
import { normalizeSiteConfigJson } from "../../Utils/siteConfig";

interface SaisieViewDialogProps {
  isOpen: boolean;
  onClose: () => void;
  site: Site;
  saisie: Saisie;
}

interface PosteWithIndicators {
  poste: {
    id: number;
    name: string;
  };
  indicators: Array<{
    id: number;
    code: string;
    libelle: string;
    unite_default: string;
    obligatoire?: boolean;
    entries: Array<{
      value: string;
      selections: DimensionSelection[];
    }>;
  }>;
}

export const SaisieViewDialog: React.FC<SaisieViewDialogProps> = ({
  isOpen,
  onClose,
  site,
  saisie,
}) => {
  const [activePosteId, setActivePosteId] = useState<number | null>(null);

  // Fetch data
  const { data: postesEmission } = useGetPostesEmission();
  const { data: typeIndicateurs } = useGetTypeIndicateurs();

  // Fetch dimensions for the active poste
  const { data: posteDimensions } = useGetPosteDimensions({
    posteId: activePosteId || 0,
    enabled: !!activePosteId,
  });

  // Organize indicators by poste with values
  const posteWithIndicators = useMemo((): PosteWithIndicators[] => {
    if (!postesEmission || !typeIndicateurs) return [];

    // Use site config_json which contains obligatoire information
    const siteConfig = normalizeSiteConfigJson(site.config_json);
    if (siteConfig.length === 0) return [];

    // Create a map of indicators by ID for quick lookup
    const indicatorsMap = new Map<number, TypeIndicateur>(
      (typeIndicateurs || []).map((indicator: TypeIndicateur) => [indicator.id, indicator])
    );

    // Create a map of values by champ_metier from postes_data
    const valuesMap = new Map<number, Array<{ value: string; selections: DimensionSelection[] }>>();
    Object.values(saisie.postes_data || {}).forEach((posteValeurs) => {
      posteValeurs.forEach((valeur) => {
        if (!valuesMap.has(valeur.champ_metier)) {
          valuesMap.set(valeur.champ_metier, []);
        }
        valuesMap.get(valeur.champ_metier)!.push({
          value: valeur.valeur.toString(),
          selections: valeur.selections || [],
        });
      });
    });

    // Create a map of postes by ID for quick lookup
    const postesMap = new Map(postesEmission.map((poste) => [poste.id, poste]));

    // Build the structure from site config
    return siteConfig
      .map((config) => {
        const poste = postesMap.get(config.poste);
        if (!poste) return null;

        // Handle both detailed config (with obligatoire) and basic config
        const configIndicateurs = Array.isArray(config.indicateurs) ? config.indicateurs : [];

        const indicators = configIndicateurs
          .map((indicatorItem: any) => {
            // Handle both formats: number (basic) or object (detailed)
            const indicatorId =
              typeof indicatorItem === "number"
                ? indicatorItem
                : indicatorItem.id || indicatorItem.indicateur_id; // Handle both id and indicateur_id
            const obligatoire =
              typeof indicatorItem === "object" ? indicatorItem.obligatoire : false;

            const indicator = indicatorsMap.get(indicatorId);
            if (!indicator) return null;

            const entries = valuesMap.get(indicatorId) || [];
            return {
              id: indicator.id,
              code: indicator.code,
              libelle: indicator.libelle,
              unite_default: indicator.unite_default,
              obligatoire,
              entries,
            };
          })
          .filter((indicator: any): indicator is NonNullable<typeof indicator> =>
            Boolean(indicator)
          );

        return {
          poste: {
            id: poste.id,
            name: poste.name,
          },
          indicators: indicators,
        };
      })
      .filter((item): item is PosteWithIndicators => Boolean(item))
      .filter((item) => item.indicators.length > 0);
  }, [postesEmission, typeIndicateurs, site.config_json, saisie.postes_data]);

  // Set first poste as active when data changes
  useEffect(() => {
    if (posteWithIndicators.length > 0 && !activePosteId) {
      setActivePosteId(posteWithIndicators[0].poste.id);
    }
  }, [posteWithIndicators, activePosteId]);

  const getMonthName = (month: number) => {
    const months = [
      "Janvier",
      "Février",
      "Mars",
      "Avril",
      "Mai",
      "Juin",
      "Juillet",
      "Août",
      "Septembre",
      "Octobre",
      "Novembre",
      "Décembre",
    ];
    return months[month - 1];
  };

  const getStatusDisplayName = (status: string) => {
    switch (status) {
      case "en_attente":
        return "En attente";
      case "valide_partiellement":
        return "Validé partiellement";
      case "valide":
        return "Validé";
      case "rejete":
        return "Rejeté";
      default:
        return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "en_attente":
        return "#f59e0b"; // amber
      case "valide_partiellement":
        return "#3b82f6"; // blue
      case "valide":
        return "#10b981"; // green
      case "rejete":
        return "#ef4444"; // red
      default:
        return "#6b7280"; // gray
    }
  };

  if (!isOpen) return null;

  return (
    <div className="dialog-overlay">
      <div className="dialog-container">
        <div className="dialog-header">
          <div className="dialog-title-section">
            <h2 className="dialog-title">
              <Eye size={20} />
              Consultation de Saisie
            </h2>
            <p className="dialog-subtitle">
              Site: {site.name} • {getMonthName(saisie.mois)} {saisie.annee}
            </p>
            <div className="status-info" style={{ marginTop: "8px" }}>
              <span
                className="status-badge"
                style={{
                  backgroundColor: getStatusColor(saisie.statut),
                  color: "white",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  fontSize: "12px",
                  fontWeight: "500",
                }}
              >
                {getStatusDisplayName(saisie.statut)}
              </span>
              {saisie.require_double_validation && (
                <span
                  className="double-validation-badge"
                  style={{
                    marginLeft: "8px",
                    fontSize: "12px",
                    color: "#6b7280",
                  }}
                >
                  Double validation requise
                </span>
              )}
            </div>
          </div>
          <button className="dialog-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="dialog-content">
          {/* Date Display */}
          <div className="date-selection">
            <div className="date-field">
              <label>
                <Calendar size={16} />
                Période
              </label>
              <div className="readonly-field">
                {getMonthName(saisie.mois)} {saisie.annee}
              </div>
            </div>
            <div className="date-field">
              <label>Date de création</label>
              <div className="readonly-field">
                {new Date(saisie.date_creation).toLocaleDateString("fr-FR")}
              </div>
            </div>
          </div>

          {/* Indicators by Poste - Read Only */}
          <div className="indicators-section">
            {posteWithIndicators.length === 0 ? (
              <div className="empty-indicators">
                <p>Aucun indicateur configuré pour ce site</p>
              </div>
            ) : (
              <>
                {/* Tabs for postes */}
                <div className="postes-tabs">
                  {posteWithIndicators.map(({ poste, indicators }) => (
                    <button
                      key={poste.id}
                      className={`poste-tab ${activePosteId === poste.id ? "active" : ""}`}
                      onClick={() => setActivePosteId(poste.id)}
                    >
                      {poste.name}
                      <span className="poste-tab-badge">{indicators.length}</span>
                    </button>
                  ))}
                </div>

                {/* Active poste content */}
                <div className="postes-list">
                  {posteWithIndicators.map(({ poste, indicators }) => (
                    <div
                      key={poste.id}
                      className={`poste-panel ${activePosteId === poste.id ? "active" : ""}`}
                    >
                      <div className="poste-content">
                        <div className="indicators-grid">
                          {indicators.map((indicator) => {
                            return (
                              <div key={indicator.id} className="indicator-card">
                                <div className="indicator-info">
                                  <div className="indicator-label">
                                    <span className="indicator-code">{indicator.code}</span>
                                    <span className="indicator-name">{indicator.libelle}</span>
                                    {indicator.obligatoire && (
                                      <span className="required-badge">*</span>
                                    )}
                                  </div>
                                </div>

                                {/* Display all entries */}
                                {indicator.entries.length === 0 ? (
                                  <div className="empty-entry-placeholder">Aucune saisie</div>
                                ) : (
                                  <div className="entries-container">
                                    {indicator.entries.map((entry, entryIndex) => {
                                      const indicatorSelections = entry.selections || [];
                                      return (
                                        <div key={entryIndex} className="indicator-entry readonly">
                                          {/* Display dimension selections */}
                                          {indicatorSelections.length > 0 &&
                                            posteDimensions &&
                                            posteDimensions.length > 0 && (
                                              <div className="dimensions-row">
                                                {indicatorSelections.map((selection) => {
                                                  const dimension = posteDimensions.find(
                                                    (d) => d.id === selection.dimension_id
                                                  );
                                                  const valeur = dimension?.valeurs.find(
                                                    (v) => v.id === selection.valeur_id
                                                  );
                                                  return (
                                                    <div
                                                      key={selection.dimension_id}
                                                      className="dimension-readonly-item"
                                                    >
                                                      <span className="dimension-readonly-label">
                                                        {dimension?.name}:
                                                      </span>
                                                      <span className="dimension-readonly-value">
                                                        {valeur?.valeur || "—"}
                                                      </span>
                                                    </div>
                                                  );
                                                })}
                                              </div>
                                            )}

                                          <div className="value-input-row">
                                            <div className="readonly-value">
                                              {entry.value || "—"}
                                            </div>
                                            <span className="indicator-unit">
                                              {indicator.unite_default}
                                            </span>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Dialog Actions */}
        <div className="dialog-actions">
          <button className="btn-cancel" onClick={onClose}>
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default SaisieViewDialog;
